// useAppVisibility.js
import { useEffect, useRef, useState } from "react";
import { useSelector } from 'react-redux';

export function useAppVisibility(opts = {}) {
  const { onWake, onSleep, wakeDebounceMs = 150 } = opts;

  const [isVisible, setIsVisible] = useState(
    typeof document !== "undefined" ? document.visibilityState === "visible" : true
  );
  const [isFocused, setIsFocused] = useState(
    typeof document !== "undefined" ? (document.hasFocus ? document.hasFocus() : true) : true
  );
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );

  const [lastChangeAt, setLastChangeAt] = useState(new Date());

  const wakeTimer = useRef(null);

  const scheduleWake = (reason) => {
    if (wakeTimer.current != null) window.clearTimeout(wakeTimer.current);
    wakeTimer.current = window.setTimeout(() => {
      if (onWake) onWake(reason);
      setLastChangeAt(new Date());
      wakeTimer.current = null;
    }, wakeDebounceMs);
  };

  useEffect(() => {
    const onVisibility = () => {
      const vis = document.visibilityState === "visible";
      setIsVisible(vis);
      setLastChangeAt(new Date());
      vis ? scheduleWake("visible") : onSleep && onSleep("hidden");
    };

    const onFocus = () => {
      setIsFocused(true);
      setLastChangeAt(new Date());
      scheduleWake("focus");
    };
    const onBlur = () => {
      setIsFocused(false);
      setLastChangeAt(new Date());
      onSleep && onSleep("blur");
    };

    const onOnline = () => {
      setIsOnline(true);
      setLastChangeAt(new Date());
      scheduleWake("online");
    };
    const onOffline = () => {
      setIsOnline(false);
      setLastChangeAt(new Date());
      onSleep && onSleep("offline");
    };

    const onPageShow = (e) => {
      // Fires on bfcache restores in Safari/iOS
      if (e.persisted) scheduleWake("pageshow");
    };
    const onPageHide = () => onSleep && onSleep("pagehide");

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    window.addEventListener("blur", onBlur);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("pagehide", onPageHide);

    return () => {
      if (wakeTimer.current != null) window.clearTimeout(wakeTimer.current);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [wakeDebounceMs, onWake, onSleep]);

  return { isVisible, isFocused, isOnline, lastChangeAt };
}
